import { useAuth } from "@/hooks/use-auth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Mail, AlertTriangle, CheckCircle } from "lucide-react";

/**
 * A banner that displays if email verification is needed
 * Only shown to logged-in users who have not verified their email
 */
export function VerificationBanner() {
  const { user, needsVerification, resendVerificationEmail } = useAuth();

  if (!user || !needsVerification) {
    return null;
  }

  return (
    <Alert className="mb-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30">
      <AlertTriangle className="h-5 w-5 text-amber-500" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-2">
        <div>
          <AlertTitle className="text-amber-800 dark:text-amber-300">Email Verification Required</AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-400">
            Please verify your email address to access all features. Check your inbox for a verification link.
          </AlertDescription>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          className="ml-auto border-amber-500 text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/50"
          onClick={() => resendVerificationEmail.mutate()}
          disabled={resendVerificationEmail.isPending}
        >
          <Mail className="mr-2 h-4 w-4" />
          {resendVerificationEmail.isPending ? 'Sending...' : 'Resend Email'}
        </Button>
      </div>
    </Alert>
  );
}

/**
 * A banner that shows when a user is successfully verified
 * Used in auth flows to show successful verification
 */
export function VerificationSuccessBanner() {
  return (
    <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950/30">
      <CheckCircle className="h-5 w-5 text-green-500" />
      <div>
        <AlertTitle className="text-green-800 dark:text-green-300">Email Verified Successfully</AlertTitle>
        <AlertDescription className="text-green-700 dark:text-green-400">
          Your email has been verified. You now have full access to all features.
        </AlertDescription>
      </div>
    </Alert>
  );
}

/**
 * A banner that shows verification errors
 */
export function VerificationErrorBanner({ message }: { message: string }) {
  return (
    <Alert className="mb-4 border-red-500 bg-red-50 dark:bg-red-950/30">
      <AlertTriangle className="h-5 w-5 text-red-500" />
      <div>
        <AlertTitle className="text-red-800 dark:text-red-300">Verification Failed</AlertTitle>
        <AlertDescription className="text-red-700 dark:text-red-400">
          {message || "There was a problem verifying your email address. Please try again or contact support."}
        </AlertDescription>
      </div>
    </Alert>
  );
}